import { DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty, keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect from 'dashboard/components/ReactSelect';
import { MonoSpan } from 'dashboard/components/Typography';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import DashboardDatePicker from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { User, UserStatus } from 'dashboard/models/user';
import { UserSession, UserSessionStatus } from 'dashboard/models/user-session';
import { UserSessionsFilterParams } from 'dashboard/redux/actions/user-sessions';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const DateFilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
  }
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]};
  align-items: flex-end;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const IconTableTH = styled(TableTH)`
  padding: 0;
`;

const IconTableTD = styled(TableTD)`
  padding: 0;
  width: 32px;
`;

const StyledIconCircleChecked = styled(CircleCheckIcon).attrs({ color: Color.BorderIconSuccess })`
  margin-top: 50%;
`;

const CardTitleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: UserSessionStatus.Active,
  },
  {
    label: 'Expired',
    value: UserSessionStatus.Expired,
  },
  {
    label: 'Pending',
    value: UserSessionStatus.Pending,
  },
];

interface DashboardUserSessionsViewProps {
  currentUser: User | undefined;
  currentUserSession: UserSession | undefined;
  userSessions: UserSession[];
  users: User[];
  isLoading: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  onPageSizeClick: (pageSize: number) => void;
  onPageLinkClick: (pageNumber: number) => void;
  filters: UserSessionsFilterParams;
  onDateUpdate: (range: RangeValue<DateValue> | null) => void;
  onUserFilterUpdate: (userIds: string[]) => void;
  onStatusFilterUpdate: (status: UserSessionStatus) => void;
  onExpire: (userSessionId: string) => Promise<void>;
  onExpireAll: (userIds: string[]) => Promise<void>;
}

const DashboardUserSessionsView = ({
  currentUser,
  currentUserSession,
  userSessions,
  users,
  isLoading,
  pageSize,
  paginationParams,
  onPageSizeClick,
  onPageLinkClick,
  filters,
  onDateUpdate,
  onUserFilterUpdate,
  onStatusFilterUpdate,
  onExpire,
  onExpireAll,
}: DashboardUserSessionsViewProps) => {
  const { createdAtStart, createdAtEnd, status, userIds = [] } = filters;

  const hasUsedFilters = ![createdAtStart, createdAtEnd, status, userIds].every(isEmpty);

  // Don't show any users unless we're showing users besides the current user
  const usersToShow = useMemo(
    () => users.filter((user) => user.attributes.status === UserStatus.Active),
    [users],
  );

  const userMap = useMemo(() => keyBy('id', usersToShow), [usersToShow]);

  const userFilterOptions = useMemo(
    () =>
      usersToShow.map(({ id, attributes: { 'email-address': emailAddress } }) => ({
        label: emailAddress,
        value: id,
      })),
    [usersToShow],
  );

  const userFilterLabelTextMapper = useCallback(
    (values: string[]) => {
      if (values.length === userFilterOptions.length) {
        return 'All Users';
      } else if (values.length === 1) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return userFilterOptions.find((v) => v.value === values[0]).label;
      } else if (values.length > 1) {
        return `${values.length} Users`;
      } else {
        return 'All Users';
      }
    },
    [userFilterOptions],
  );

  const [showExpireAllModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleExpireAllForUsers = useCallback(
    () =>
      prompt(async () => {
        await onExpireAll(userIds);
        onUserFilterUpdate([]);
      }),
    [userIds],
  );

  const handleExpireAllForSelf = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    async () => await onExpireAll([currentUser.id]),
    [currentUser],
  );

  return (
    <Container>
      <Header title='Sessions'></Header>
      <Section>
        <ModalWrapper isOpen={showExpireAllModal} handleCloseModal={cancel}>
          <ConfirmationModal
            cancelAction={cancel}
            confirmAction={confirm}
            header='Sign out selected users?'
            confirmActionText='Sign out'
          >
            <p>{`This will sign the following users out of all active sessions: ${userIds
              .map((id) => userMap[id]?.attributes['email-address'])
              .filter(Boolean)
              .join(', ')}`}</p>
          </ConfirmationModal>
        </ModalWrapper>

        <Card>
          <Card.Body>
            <DateFilterRow>
              <DashboardDatePicker
                dataTest='user-sessions-view__date-range'
                handleDatesChange={onDateUpdate}
                dateRange={{
                  startDate: createdAtStart,
                  endDate: createdAtEnd,
                }}
                size='md'
              />
            </DateFilterRow>
            <FilterRow>
              <div>
                <FormControlLabel>Status</FormControlLabel>

                <ReactSelect
                  value={status}
                  options={STATUS_FILTER_OPTIONS}
                  onChange={onStatusFilterUpdate}
                  data-test='user-sessions-view__status'
                />
              </div>
              <div>
                <FormControlLabel>Users</FormControlLabel>

                <MultiSelect
                  value={userIds}
                  selectOptionValues={userFilterOptions}
                  labelTextMapper={userFilterLabelTextMapper}
                  onChange={onUserFilterUpdate}
                  data-test='user-sessions-view__select-user'
                  size='md'
                />
              </div>
              <Permissioned permission={Permission.UserSessionsWrite} fallback={null}>
                <Button
                  id='user-sessions-view__expire-all-team'
                  data-test='user-sessions-view__expire-all-team'
                  variant='danger'
                  onClick={handleExpireAllForUsers}
                  disabled={userIds.length === 0}
                >
                  Sign out selected users ({userIds.length})
                </Button>
              </Permissioned>
            </FilterRow>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <CardTitleContainer>
              <Card.Title>Sessions</Card.Title>

              <Button
                id='user-sessions-view__expire-all-self'
                data-test='user-sessions-view__expire-all-self'
                width='auto'
                variant='danger'
                onClick={handleExpireAllForSelf}
              >
                Sign out other sessions
              </Button>
            </CardTitleContainer>
            <TableLayout
              dataTest='user-sessions-view__table'
              isLoading={isLoading}
              isEmpty={userSessions.length === 0}
              showHeader={hasUsedFilters}
              headerTR={
                <>
                  <IconTableTH>{/* Is current session */}</IconTableTH>
                  <TableTH>User</TableTH>
                  <TableTH>IP address</TableTH>
                  <TableTH>{`Created at (${DateTime.local().toFormat(
                    TIMEZONE_NAME_ABBREVIATED,
                  )})`}</TableTH>
                  <TableTH>{`Expired at (${DateTime.local().toFormat(
                    TIMEZONE_NAME_ABBREVIATED,
                  )})`}</TableTH>
                  <TableTH>User agent</TableTH>
                  <TableTH>{/* Expire */}</TableTH>
                </>
              }
              nullState={
                <TableNullState dataTestBlock='user-sessions-view' hasUsedFilters={hasUsedFilters}>
                  No user sessions have been created yet.
                </TableNullState>
              }
            >
              {userSessions.map((userSession) => (
                <TableTR
                  key={userSession.id}
                  id={`user-session-table-row__${userSession.id}`}
                  data-test={`user-session-table-row__${userSession.id}`}
                >
                  <IconTableTD>
                    {userSession.id === currentUserSession?.id && (
                      <HelpIcon
                        icon={<StyledIconCircleChecked />}
                        id={`user-session-table-row__${userSession.id}--current-session`}
                        data-test={`user-session-table-row__${userSession.id}--current-session`}
                      >
                        This is your currently active session.
                      </HelpIcon>
                    )}
                  </IconTableTD>
                  <TableTD data-test={`user-session-table-row__${userSession.id}--email`}>
                    {userMap[userSession.relationships.user.data.id]?.attributes['email-address']}
                  </TableTD>
                  <TableTD>
                    <MonoSpan>{userSession.attributes['ip-address']}</MonoSpan>
                  </TableTD>
                  <TableTD>
                    {DateTime.fromISO(userSession.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    )}
                  </TableTD>
                  <TableTD>
                    {userSession.attributes['expired-at'] != null &&
                      DateTime.fromISO(userSession.attributes['expired-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )}
                  </TableTD>
                  <TableTD>{userSession.attributes['user-agent']}</TableTD>
                  <TableActionTD>
                    {userSession.attributes.status !== UserSessionStatus.Expired &&
                      userSession.id !== currentUserSession?.id && (
                        <Button
                          onClick={() => onExpire(userSession.id)}
                          id={`user-session-table-row__${userSession.id}--expire`}
                          data-test={`user-session-table-row__${userSession.id}--expire`}
                          variant='danger'
                        >
                          Sign out
                        </Button>
                      )}
                  </TableActionTD>
                </TableTR>
              ))}
            </TableLayout>
            <Pagination
              pageSize={pageSize}
              paginationParams={paginationParams}
              handlePageSizeClick={onPageSizeClick}
              handlePageLinkClick={onPageLinkClick}
            />
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardUserSessionsView;
